import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { useDispatch } from 'react-redux';
import {
  CompanyBillingInterval,
  CompanyPlan,
  CompanyRegistrationRequest,
  UserAuthResponse,
} from '@handover/shared-types';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { useLoginMutation, useRegisterCompanyMutation } from '../../../redux/auth/authApi';
import { setAuthState } from '../../../redux/auth/authSlice';
import BasePage from '../authentication-layout';
import ProjectLogo from '../../../components/commons/ProjectLogo';
import { projectConfig } from '../../../config/projectConfig';
import { saveToken } from '../../../helpers/tokenStorage';
import { AuthError, isErrorResponse, isFirebaseError, getAuthErrorMessageKey } from '../../../helpers/authErrorHelper';
import { useTranslation } from 'react-i18next';
import {
  COMPANY_REGISTRATION_ENABLED,
} from '../../../constants/constant';
import {
  useGetCompanyPlansQuery,
} from '../../../redux/company/companyApi';

type RegistrationForm = CompanyRegistrationRequest & {
  confirmPassword: string;
};

const initialForm: RegistrationForm = {
  companyName: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
  position: '',
  phoneNumber: '',
  bio: '',
};

const MIN_PASSWORD_LENGTH = 12;
const MAX_PASSWORD_LENGTH = 64;
const TERMS_LAST_UPDATED = 'March 11, 2026';
const SELF_SERVE_TRIAL_DAYS = 14;
const ENTERPRISE_CONTACT_URL =
  'https://calendly.com/handoverengine-info/handover-engine-demo-1';

const TERMS_OF_SERVICE_SECTIONS = [
  {
    title: '1. Service Scope',
    body: [
      'Handover Engine provides a company workspace for preparing, reviewing,',
      'approving, publishing, and archiving employee handovers. The service may',
      'include questionnaires, file uploads, AI-assisted draft generation,',
      'incoming views, audit history, and billing controls.',
    ].join(' '),
  },
  {
    title: '2. Company Account Responsibility',
    body: [
      'The person completing this registration is creating the initial company',
      'account and confirms they are authorized to bind the company to these',
      'terms. The company is responsible for all activity within its workspace,',
      'including user access, submitted content, and administrative actions.',
    ].join(' '),
  },
  {
    title: '3. User Access and Security',
    body: [
      'The company must maintain accurate user records, protect login',
      'credentials, enforce appropriate internal access controls, and promptly',
      'remove access for unauthorized or departed users. Handover Engine may',
      'rely on the permissions configured inside the workspace.',
    ].join(' '),
  },
  {
    title: '4. Customer Data',
    body: [
      'The company retains responsibility for the data, files, handovers, and',
      'employee information submitted to the service. The company must ensure it',
      'has the right to upload and process that information and must avoid',
      'submitting unlawful, infringing, or unauthorized material.',
    ].join(' '),
  },
  {
    title: '5. AI-Assisted Content',
    body: [
      'AI-assisted drafting is provided to help structure handover content, but',
      'generated output may be incomplete or inaccurate. The company is',
      'responsible for reviewing, editing, and approving any AI-assisted draft',
      'before it is relied on, shared, or published to incoming employees.',
    ].join(' '),
  },
  {
    title: '6. Acceptable Use',
    body: [
      'The service must not be used to abuse the platform, bypass access',
      'controls, interfere with system operations, reverse engineer restricted',
      'services, upload malicious files, or process content in violation of',
      'applicable law, employment obligations, privacy obligations, or',
      'third-party rights.',
    ].join(' '),
  },
  {
    title: '7. Billing, Trials, and Plan Changes',
    body: [
      'The standard Handover Engine subscription is billed at a flat monthly',
      'rate for software access. Free trials, if granted, may expire',
      'automatically. AI features may be limited to paid workspaces and may be',
      'subject to included usage allowances and additional purchase terms.',
      'Failure to maintain billing may result in restricted access to the',
      'workspace.',
    ].join(' '),
  },
  {
    title: '8. Availability and Changes',
    body: [
      'Handover Engine may improve, modify, suspend, or discontinue features',
      'from time to time. While reasonable efforts may be made to maintain',
      'availability, uninterrupted service is not guaranteed.',
    ].join(' '),
  },
  {
    title: '9. Suspension and Termination',
    body: [
      'Access may be suspended or restricted for unpaid billing, security',
      'concerns, misuse, legal compliance, or breaches of these terms. The',
      'company remains responsible for outstanding charges and obligations that',
      'arose before termination.',
    ].join(' '),
  },
  {
    title: '10. Limitation of Responsibility',
    body: [
      'To the maximum extent permitted by law, Handover Engine is provided and',
      'licensed on an "as is" and "as available" basis, and the company',
      'assumes responsibility for its use of the platform, including the review',
      'and operational use of handover content.',
    ].join(' '),
  },
  {
    title: '11. Contact',
    body: [
      'Questions about these terms or billing can be directed through the',
      'support process made available within the Handover Engine platform.',
    ].join(' '),
  },
] as const;

const registrationErrorMessages: Record<string, string> = {
  'company.register.disabled': 'Company registration is temporarily unavailable.',
  'company.register.required': 'Company name, first name, last name, email, and password are required.',
  'company.register.password.short': `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  'company.register.password.long': `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer.`,
  'company.register.email.exists': 'That email already belongs to a company account.',
};

const getPasswordValidationMessage = (password: string): string | null => {
  if (!password) {
    return null;
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    return `Use ${MAX_PASSWORD_LENGTH} characters or fewer.`;
  }

  return null;
};

const formatCurrencyAmount = (amountCents?: number, currency = 'usd'): string => {
  if (typeof amountCents !== 'number') {
    return 'Custom';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  }).format(amountCents / 100);
};

const getPlanIntervalDetails = (
  plan: CompanyPlan | null,
  interval: CompanyBillingInterval
) => plan?.intervals?.[interval];

const getPlanPricingLine = (
  plan: CompanyPlan,
  interval: CompanyBillingInterval
): string => {
  const selectedInterval = getPlanIntervalDetails(plan, interval);

  if (!selectedInterval?.amountCents) {
    return 'Contact sales';
  }

  if (interval === 'monthly') {
    return `${formatCurrencyAmount(
      selectedInterval.amountCents,
      selectedInterval.currency
    )}/month`;
  }

  return `${formatCurrencyAmount(
    selectedInterval.amountCents / 12,
    selectedInterval.currency
  )}/month billed annually`;
};

const getPlanBillingNote = (
  plan: CompanyPlan,
  interval: CompanyBillingInterval
): string => {
  const selectedInterval = getPlanIntervalDetails(plan, interval);

  if (!selectedInterval?.amountCents) {
    return 'Custom pricing and onboarding.';
  }

  if (interval === 'monthly') {
    return `${formatCurrencyAmount(
      selectedInterval.amountCents,
      selectedInterval.currency
    )} charged each month.`;
  }

  return `${formatCurrencyAmount(
    selectedInterval.amountCents,
    selectedInterval.currency
  )} charged annually.`;
};

const CompanyRegisterPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { userAuth } = useAppSelector(state => state.auth);
  const navigate = useNavigate();
  const [form, setForm] = useState<RegistrationForm>(initialForm);
  const [selectedPlanKey, setSelectedPlanKey] = useState<CompanyPlan['key']>('team');
  const [billingInterval, setBillingInterval] = useState<CompanyBillingInterval>('monthly');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const [messageType, setMessageType] = useState<'success' | 'error'>('error');
  const [registerCompany, { isLoading: isRegistering }] = useRegisterCompanyMutation();
  const [login, { isLoading: isSigningIn }] = useLoginMutation();
  const { data: plansResponse } = useGetCompanyPlansQuery(undefined, {
    skip: !COMPANY_REGISTRATION_ENABLED,
  });

  const availablePlans = plansResponse?.plans || [];
  const stripeBillingEnabled = plansResponse?.stripeBillingEnabled ?? false;
  const stripeSetup = plansResponse?.stripeSetup;
  const selectedPlan = useMemo(
    () => availablePlans.find(plan => plan.key === selectedPlanKey) || availablePlans[0] || null,
    [availablePlans, selectedPlanKey]
  );
  const availableBillingIntervals = useMemo(
    () =>
      (['monthly', 'annual'] as CompanyBillingInterval[]).filter(interval =>
        Boolean(selectedPlan?.intervals?.[interval])
      ),
    [selectedPlan]
  );
  const selectedPlanInterval = useMemo(
    () => getPlanIntervalDetails(selectedPlan, billingInterval),
    [billingInterval, selectedPlan]
  );
  const isEnterpriseContactPlan = Boolean(
    selectedPlan && !selectedPlan.selfServeCheckoutEnabled
  );
  const passwordValidationMessage = useMemo(
    () => getPasswordValidationMessage(form.password),
    [form.password]
  );

  const getErrorMessage = (error: AuthError): string => {
    if (isErrorResponse(error)) {
      const backendMessage = error.data?.message;
      if (backendMessage && registrationErrorMessages[backendMessage]) {
        return registrationErrorMessages[backendMessage];
      }
      if (backendMessage) {
        const translated = t(backendMessage);
        return translated === backendMessage ? backendMessage : translated;
      }
      return 'Unable to create your company account.';
    }

    if (isFirebaseError(error)) {
      return t(getAuthErrorMessageKey(error));
    }

    return 'Unable to create your company account.';
  };

  useEffect(() => {
    if (userAuth) {
      navigate('/my-work', { replace: true });
    }
  }, [navigate, userAuth]);

  useEffect(() => {
    if (!COMPANY_REGISTRATION_ENABLED) {
      navigate('/auth/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!selectedPlan && availablePlans.length > 0) {
      setSelectedPlanKey(availablePlans[0].key);
    }
  }, [availablePlans, selectedPlan]);

  useEffect(() => {
    if (availableBillingIntervals.length > 0 && !availableBillingIntervals.includes(billingInterval)) {
      setBillingInterval(availableBillingIntervals[0]);
    }
  }, [availableBillingIntervals, billingInterval]);

  const completeRegistration = async (
    payload: CompanyRegistrationRequest,
    password: string
  ) => {
    await registerCompany(payload).unwrap();

    const loginResponse = await login({
      email: payload.email.trim().toLowerCase(),
      password,
    }).unwrap();

    const { userAuth: authenticatedUser, token } = loginResponse as UserAuthResponse;
    if (token) {
      saveToken(token);
    }

    if (authenticatedUser) {
      dispatch(setAuthState({ userAuth: authenticatedUser }));
      navigate('/my-work', { replace: true });
      return;
    }

    setMessageType('success');
    setMessage('Company created. Sign in with your new account.');
  };

  const isSubmitDisabled = useMemo(() => {
    const hasRequiredFields =
      form.companyName.trim() &&
      form.firstName.trim() &&
      form.lastName.trim() &&
      form.email.trim() &&
      form.password &&
      form.confirmPassword;

    return (
      !hasRequiredFields ||
      Boolean(passwordValidationMessage) ||
      form.password !== form.confirmPassword ||
      !acceptedTerms ||
      !selectedPlan ||
      !selectedPlanInterval ||
      isRegistering ||
      isSigningIn
    );
  }, [
    form,
    acceptedTerms,
    isRegistering,
    isSigningIn,
    passwordValidationMessage,
    selectedPlan,
    selectedPlanInterval,
  ]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm(current => ({
      ...current,
      [name]: value,
    }));
    if (message) {
      setMessage(undefined);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (passwordValidationMessage) {
      setMessageType('error');
      setMessage(passwordValidationMessage);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMessageType('error');
      setMessage('Passwords must match.');
      return;
    }

    if (!acceptedTerms) {
      setMessageType('error');
      setMessage('You must accept the Terms of Service before continuing.');
      return;
    }

    try {
      const registrationPayload: CompanyRegistrationRequest = {
        companyName: form.companyName.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        planKey: selectedPlanKey,
        billingInterval,
        position: form.position?.trim() || '',
        phoneNumber: form.phoneNumber?.trim() || '',
        bio: form.bio?.trim() || '',
      };

      await completeRegistration(registrationPayload, form.password);
    } catch (error) {
      setMessageType('error');
      setMessage(getErrorMessage(error as AuthError));
    }
  };

  return (
    <BasePage image={projectConfig.bgLogin}>
      <Grid2 container justifyContent="center" alignItems="center" minHeight="100vh" px={1.5}>
        <Grid2 size={{ xs: 12, lg: 10, xl: 9 }}>
          <Card
            sx={theme => ({
              overflow: 'hidden',
              borderRadius: 4,
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(7,18,36,0.96), rgba(11,32,58,0.92))'
                : 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(240,247,255,0.94))',
              boxShadow: `0 24px 80px ${theme.HEPalette.coloredShadows.dark}`,
              border: `1px solid ${theme.palette.divider}`,
            })}
          >
            <Grid2 container>
              <Grid2 size={{ xs: 12, md: 5 }}>
                <Box
                  sx={theme => ({
                    height: '100%',
                    p: { xs: 3, md: 5 },
                    color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary,
                    background: theme.palette.mode === 'dark'
                      ? 'radial-gradient(circle at top left, rgba(2,136,209,0.2), rgba(8,18,35,0.96) 60%)'
                      : 'linear-gradient(180deg, rgba(2,136,209,0.08), rgba(2,136,209,0.02))',
                  })}
                >
                  <ProjectLogo size="card" />
                  <Chip
                    label="New Company Setup"
                    color="primary"
                    sx={{ mt: 3, mb: 2, fontWeight: 700 }}
                  />
                  <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
                    Launch your company workspace with one account.
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, opacity: 0.82, maxWidth: 420 }}>
                    This creates the company boundary, provisions the initial account,
                    and keeps handovers, departures, incoming views, and audit data scoped to that company.
                  </Typography>

                  <Stack spacing={1.5} sx={{ mt: 4 }}>
                    <Chip label="Company-scoped data" variant="outlined" />
                    <Chip label="Unlimited users" variant="outlined" />
                    <Chip label={`${SELF_SERVE_TRIAL_DAYS}-day trial with no card`} variant="outlined" />
                  </Stack>
                </Box>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 7 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 3, md: 5 } }}>
                  <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    Create Company
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                    Start with the company name and the first administrator who will own the workspace.
                  </Typography>

                  {message && (
                    <Alert severity={messageType} sx={{ mb: 3 }}>
                      {message}
                    </Alert>
                  )}

                  <Box
                    sx={theme => ({
                      mb: 3,
                      p: 2.5,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15, 23, 42, 0.55)'
                          : 'rgba(248, 250, 252, 0.9)',
                    })}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                      Workspace Plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      One flat software subscription with unlimited users. AI drafting and assistant features unlock after the workspace becomes paid.
                    </Typography>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.5}
                      sx={{ mt: 2 }}
                    >
                      {availableBillingIntervals.map(interval => (
                        <Button
                          key={interval}
                          type="button"
                          variant={billingInterval === interval ? 'contained' : 'outlined'}
                          color={billingInterval === interval ? 'primary' : 'inherit'}
                          onClick={() => setBillingInterval(interval)}
                          sx={{ flex: 1, py: 1.3, fontWeight: 700 }}
                        >
                          <Stack spacing={0.25} sx={{ textAlign: 'left' }}>
                            <Typography variant="button" sx={{ color: 'inherit', fontWeight: 700 }}>
                              {interval === 'annual' ? 'Annual Plan' : 'Monthly Plan'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.9 }}>
                              {interval === 'annual'
                                ? 'Save 17% with annual billing'
                                : 'Flat monthly software subscription'}
                            </Typography>
                          </Stack>
                        </Button>
                      ))}
                    </Stack>
                    <Grid2 container spacing={1.5} sx={{ mt: 2 }}>
                      {availablePlans.map(plan => {
                        const intervalDetails = getPlanIntervalDetails(plan, billingInterval);
                        const isSelected = selectedPlanKey === plan.key;
                        const isAvailable = Boolean(intervalDetails);
                        const seatLabel = plan.unlimitedSeats
                          ? 'Unlimited users'
                          : `${plan.includedSeats} included seats`;

                        return (
                          <Grid2 key={plan.key} size={{ xs: 12, md: 4 }}>
                            <Box
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                if (isAvailable) {
                                  setSelectedPlanKey(plan.key);
                                }
                              }}
                              onKeyDown={event => {
                                if (isAvailable && (event.key === 'Enter' || event.key === ' ')) {
                                  event.preventDefault();
                                  setSelectedPlanKey(plan.key);
                                }
                              }}
                              sx={theme => ({
                                height: '100%',
                                p: 2,
                                borderRadius: 3,
                                cursor: isAvailable ? 'pointer' : 'not-allowed',
                                border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                                backgroundColor: isSelected
                                  ? theme.palette.mode === 'dark'
                                    ? 'rgba(2, 136, 209, 0.18)'
                                    : 'rgba(2, 136, 209, 0.08)'
                                  : theme.palette.mode === 'dark'
                                    ? 'rgba(15, 23, 42, 0.62)'
                                    : theme.palette.common.white,
                                boxShadow: isSelected
                                  ? `0 14px 36px ${theme.HEPalette.coloredShadows.primary}`
                                  : 'none',
                                opacity: isAvailable ? 1 : 0.6,
                                transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                                '&:hover': isAvailable
                                  ? {
                                      transform: 'translateY(-2px)',
                                      borderColor: theme.palette.primary.main,
                                    }
                                  : undefined,
                              })}
                                >
                                  <Stack spacing={1.25} sx={{ height: '100%' }}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                  alignItems="flex-start"
                                  spacing={1}
                                >
                                  <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                      {plan.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                      {plan.description}
                                    </Typography>
                                  </Box>
                                  {isSelected ? (
                                    <Chip label="Selected" color="primary" size="small" />
                                  ) : !plan.selfServeCheckoutEnabled ? (
                                    <Chip label="Contact Us" color="secondary" size="small" />
                                  ) : null}
                                </Stack>

                                <Box>
                                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                    {getPlanPricingLine(plan, billingInterval)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {getPlanBillingNote(plan, billingInterval)}
                                  </Typography>
                                </Box>

                                <Chip
                                  label={seatLabel}
                                  variant="outlined"
                                  sx={{ width: 'fit-content', fontWeight: 700 }}
                                />
                              </Stack>
                            </Box>
                          </Grid2>
                        );
                      })}
                    </Grid2>
                    {stripeBillingEnabled && !stripeSetup?.checkoutConfigurationReady ? (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Stripe billing has been enabled in code, but the Stripe setup is not complete yet.
                        Finish the missing billing checklist items before turning on paid registration.
                      </Alert>
                    ) : null}
                    {stripeBillingEnabled && stripeSetup?.checkoutConfigurationReady ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Start your {SELF_SERVE_TRIAL_DAYS}-day trial now with no credit card.
                        AI stays locked during trial and unlocks after you activate billing.
                      </Alert>
                    ) : null}
                    {isEnterpriseContactPlan ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Enterprise onboarding starts with a short intro call. We&apos;ll walk
                        through rollout, support, security, and billing needs before setting up
                        the workspace.
                      </Alert>
                    ) : null}
                    {!stripeBillingEnabled ? (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Billing activation is not available in this environment yet.
                        You can still create the workspace and start the trial without payment.
                      </Alert>
                    ) : null}
                  </Box>

                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        required
                        label="Company Name"
                        name="companyName"
                        value={form.companyName}
                        onChange={handleChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        required
                        label="First Name"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        required
                        label="Last Name"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Job Title"
                        name="position"
                        value={form.position}
                        onChange={handleChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        name="phoneNumber"
                        value={form.phoneNumber}
                        onChange={handleChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        required
                        type="email"
                        label="Work Email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        required
                        type="password"
                        label="Password"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        error={Boolean(passwordValidationMessage)}
                        helperText={
                          passwordValidationMessage ||
                          `Use ${MIN_PASSWORD_LENGTH}-${MAX_PASSWORD_LENGTH} characters. Passphrases and symbols are allowed.`
                        }
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        required
                        type="password"
                        label="Confirm Password"
                        name="confirmPassword"
                        value={form.confirmPassword}
                        onChange={handleChange}
                        error={Boolean(form.confirmPassword) && form.password !== form.confirmPassword}
                        helperText={
                          form.confirmPassword && form.password !== form.confirmPassword
                            ? 'Passwords must match'
                            : ' '
                        }
                      />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Short Bio"
                        name="bio"
                        value={form.bio}
                        onChange={handleChange}
                      />
                    </Grid2>
                  </Grid2>

                  <Divider sx={{ my: 3 }} />

                  <Box
                    sx={theme => ({
                      mb: 3,
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(15, 23, 42, 0.42)'
                          : 'rgba(248, 250, 252, 0.82)',
                    })}
                  >
                    <FormControlLabel
                      sx={{ alignItems: 'flex-start', m: 0 }}
                      control={
                        <Checkbox
                          checked={acceptedTerms}
                          onChange={event => setAcceptedTerms(event.target.checked)}
                          sx={{ mt: 0.25, mr: 1 }}
                        />
                      }
                      label={(
                        <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                          I agree to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() => setTermsOpen(true)}
                            sx={{ fontWeight: 700 }}
                          >
                            Terms of Service
                          </Link>
                          .
                        </Typography>
                      )}
                    />
                  </Box>

                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                  >
                    <Link component={RouterLink} to="/auth/login" underline="hover">
                      Back to sign in
                    </Link>
                    {isEnterpriseContactPlan ? (
                      <Button
                        component="a"
                        href={ENTERPRISE_CONTACT_URL}
                        target="_blank"
                        rel="noreferrer"
                        variant="contained"
                        sx={{ minWidth: 220, py: 1.4, fontWeight: 700 }}
                      >
                        Contact Us for Enterprise
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={isSubmitDisabled}
                        sx={{ minWidth: 220, py: 1.4, fontWeight: 700 }}
                      >
                        {isRegistering || isSigningIn
                          ? 'Creating workspace...'
                          : `Start ${SELF_SERVE_TRIAL_DAYS}-Day Free Trial`}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Grid2>
            </Grid2>
          </Card>
        </Grid2>
      </Grid2>
      <Dialog
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Terms of Service
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Last updated {TERMS_LAST_UPDATED}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 520 }}>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              These terms apply to company registration and ongoing use of the Handover Engine platform.
            </Typography>
            {TERMS_OF_SERVICE_SECTIONS.map(section => (
              <Box key={section.title}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {section.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {section.body}
                </Typography>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setTermsOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </BasePage>
  );
};

export default CompanyRegisterPage;
